import React from 'react';
import styles from './CalendarSearchPicker.module.css';

export type CalendarSearchPickerOption = {
  id: string;
  label: string;
  meta?: string;
};

export type CalendarSearchPickerProps = {
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  selectedCountLabel: string;
  showingCountLabel: string;
  loadMoreLabel: string;
  listAriaLabel: string;
  visibleOptions: CalendarSearchPickerOption[];
  selectedOptions: CalendarSearchPickerOption[];
  selectedIds: string[];
  search: string;
  onSearchChange: (value: string) => void;
  onToggle: (id: string) => void;
  hasMore: boolean;
  onLoadMore: () => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  closeOnSelect?: boolean;
  toolbar?: React.ReactNode;
  footerHint?: React.ReactNode;
  selectedSummaryHint?: React.ReactNode;
  className?: string;
  triggerClassName?: string;
};

export function CalendarSearchPicker({
  placeholder,
  searchPlaceholder,
  emptyMessage,
  selectedCountLabel,
  showingCountLabel,
  loadMoreLabel,
  listAriaLabel,
  visibleOptions,
  selectedOptions,
  selectedIds,
  search,
  onSearchChange,
  onToggle,
  hasMore,
  onLoadMore,
  isOpen,
  onOpenChange,
  disabled = false,
  closeOnSelect = false,
  toolbar,
  footerHint,
  selectedSummaryHint,
  className,
  triggerClassName,
}: CalendarSearchPickerProps) {
  const pickerRef = React.useRef<HTMLDivElement | null>(null);
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen, onOpenChange]);

  React.useEffect(() => {
    if (isOpen) {
      window.setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  return (
    <div
      className={[styles.picker, isOpen && styles.pickerOpen, className].filter(Boolean).join(' ')}
      data-picker-open={isOpen ? 'true' : undefined}
      ref={pickerRef}
    >
      <button
        type="button"
        className={[styles.trigger, triggerClassName].filter(Boolean).join(' ')}
        onClick={() => {
          if (disabled) {
            return;
          }

          onOpenChange(!isOpen);
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={disabled}
      >
        <div className={styles.triggerContent}>
          {selectedOptions.length > 0 ? (
            <div className={styles.chipRow}>
              {selectedOptions.slice(0, 3).map((option) => (
                <span key={option.id} className={styles.chip}>
                  <span className={styles.chipLabel}>{option.label}</span>
                </span>
              ))}
              {selectedOptions.length > 3 ? (
                <span className={styles.chipMuted}>
                  {selectedCountLabel}
                </span>
              ) : null}
            </div>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
        <span className={styles.caret} aria-hidden>{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen ? (
        <div className={styles.dropdown}>
          <input
            ref={searchInputRef}
            className="text-input"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
          />

          {toolbar}

          {selectedOptions.length > 0 ? (
            <div className={styles.selectedList}>
              {selectedOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={styles.selectedChip}
                  onClick={() => onToggle(option.id)}
                >
                  <span>{option.label}</span>
                  <span aria-hidden>×</span>
                </button>
              ))}
            </div>
          ) : null}

          <div className={styles.list} role="listbox" aria-label={listAriaLabel}>
            {visibleOptions.map((option) => {
              const checked = selectedIds.includes(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  className={`${styles.option} ${checked ? styles.optionSelected : ''}`}
                  onClick={() => {
                    onToggle(option.id);
                    if (closeOnSelect) {
                      onOpenChange(false);
                    }
                  }}
                >
                  <span className={styles.optionMain}>
                    <span className={styles.optionLabel}>{option.label}</span>
                    {option.meta ? (
                      <span className={styles.optionMeta}>{option.meta}</span>
                    ) : null}
                  </span>
                  <span className={styles.optionCheck} aria-hidden>{checked ? '✓' : ''}</span>
                </button>
              );
            })}
            {visibleOptions.length === 0 && (
              <p className={styles.inlineHint}>{emptyMessage}</p>
            )}
          </div>

          <div className={styles.footer}>
            {hasMore ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onLoadMore}
              >
                {loadMoreLabel}
              </button>
            ) : <span />}
            <p className={styles.inlineHint}>{showingCountLabel}</p>
          </div>

          {footerHint}
        </div>
      ) : null}

      {selectedSummaryHint}
    </div>
  );
}
